/**
 * useTableScroll
 * ──────────────
 * AS-IS common.js 의 "컨텐츠 테이블 스크롤 div 감싸기" 로직을
 * Vue 3 Composition API 로 전환한 전역 composable.
 *
 * - 뷰포트 ≤ 1023px : 테이블 너비가 부모보다 크면
 *   <div class="table_scroll"> + <div class="table_scroll_wrap"> 으로 감싼다.
 * - 뷰포트 > 1023px  : 감싼 div 를 제거한다.
 *
 * App.vue 의 onMounted 에서 한 번만 호출하면
 * resize · 라우트 변경 · 동적 DOM 변경에 자동 대응한다.
 */
import { onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'

export function useTableScroll() {
  const route = useRoute()

  /* ── 핵심 로직 ─────────────────────────────── */
  function tableScroll() {
    const isMobile = window.matchMedia('(max-width:1023px)').matches

    document
      .querySelectorAll('.cont_body table')
      .forEach((table) => {
        // :hidden 에 해당하는 테이블 건너뛰기
        if (table.offsetParent === null) return

        // data-no-table-scroll 속성이 있는 조상을 가진 테이블은 제외
        if (table.closest('[data-no-table-scroll]')) return

        if (isMobile) {
          // CKEditor 등 figure.table 안에 table이 있는 경우 → figure를 wrap 대상으로 삼음
          // 그렇지 않으면 .table_scroll이 figure 안에 갇혀 figure 너비로 제한됨
          const figureFigure = table.closest('figure')
          const wrapTarget = figureFigure ?? table

          const tblW = table.offsetWidth
          const wrapTargetW = wrapTarget.offsetWidth
          const parentEl = wrapTarget.parentElement
          const parentW = parentEl?.offsetWidth ?? 0
          // scrollWidth: 오버플로우된 자식 포함 실제 콘텐츠 너비
          const parentScrollW = parentEl?.scrollWidth ?? 0
          // .cont_body 기준 너비: figure 등 고정 너비 부모 안에 들어간 테이블도 감지
          const contBody = table.closest('.cont_body')
          const refW = contBody ? contBody.clientWidth : document.documentElement.clientWidth

          // 아직 레이아웃 계산 전인 경우 건너뜀 (parentW=0이면 모든 테이블이 조건 통과되어 잘못 wrapping됨)
          if (tblW === 0 || parentW === 0) return

          if (tblW > parentW || wrapTargetW > parentW || parentScrollW > parentW || tblW > refW) {
            // 아직 감싸지 않았으면 wrap
            if (!wrapTarget.parentElement?.classList.contains('table_scroll')) {
              const scrollDiv = document.createElement('div')
              scrollDiv.className = 'table_scroll'
              scrollDiv.setAttribute('tabindex', '0')

              const wrapDiv = document.createElement('div')
              wrapDiv.className = 'table_scroll_wrap'

              wrapTarget.parentNode.insertBefore(wrapDiv, wrapTarget)
              wrapDiv.appendChild(scrollDiv)
              scrollDiv.appendChild(wrapTarget)
            }
          } else {
            unwrapTable(table)
          }
        } else {
          unwrapTable(table)
        }
      })
  }

  /**
   * table_scroll / table_scroll_wrap div 를 제거하고
   * 테이블을 원래 위치로 복원한다.
   */
  function unwrapTable(table) {
    const scrollParent = table.closest('.table_scroll')
    if (!scrollParent) return

    // figure 안에 table이 있는 경우, 실제 wrap 대상은 figure
    const figureFigure = table.closest('figure')
    const wrapTarget = figureFigure && scrollParent.contains(figureFigure) ? figureFigure : table

    const wrapParent = scrollParent.closest('.table_scroll_wrap')
    const restoreParent = wrapParent?.parentNode ?? scrollParent.parentNode

    if (wrapParent) {
      restoreParent.insertBefore(wrapTarget, wrapParent)
      wrapParent.remove()
    } else {
      restoreParent.insertBefore(wrapTarget, scrollParent)
      scrollParent.remove()
    }
  }

  /* ── 이벤트 / 옵저버 ──────────────────────── */
  let resizeHandler = null
  let observer = null

  function run() {
    // nextTick 후 실행하되, 레이아웃 계산이 덜 된 경우를 위해 한 번 더 실행
    nextTick(() => {
      tableScroll()
      setTimeout(() => tableScroll(), 500)
    })
  }

  onMounted(() => {
    // 1) 최초 실행
    run()

    // 2) resize 이벤트
    resizeHandler = () => tableScroll()
    window.addEventListener('resize', resizeHandler)

    // 3) MutationObserver — 동적으로 추가되는 콘텐츠 감지
    // 조건을 좁히면 v-dompurify-html 등에서 table이 wrapper 안에 삽입되는 경우를 놓침.
    // wrapper div(table_scroll/wrap)가 아닌 요소 노드 추가가 있으면 무조건 재검사.
    const target = document.querySelector('.cont_body') ?? document.body
    let scrollTimer = null
    observer = new MutationObserver((mutations) => {
      const hasNewElement = mutations.some((m) =>
        [...m.addedNodes].some(
          (n) =>
            n.nodeType === 1 &&
            !n.classList?.contains('table_scroll') &&
            !n.classList?.contains('table_scroll_wrap')
        )
      )
      if (hasNewElement) {
        // debounce: 연속 DOM 변경 시 마지막 한 번만 실행
        clearTimeout(scrollTimer)
        scrollTimer = setTimeout(() => tableScroll(), 300)
      }
    })
    observer.observe(target, { childList: true, subtree: true })
  })

  // 4) 라우트 변경 시 재실행
  watch(() => route.fullPath, () => {
    // DOM 업데이트 후 실행 — API 응답 대기를 감안해 딜레이를 충분히 줌
    // MutationObserver가 API 응답 후 테이블 삽입도 감지하므로 이 타이머는 초기 렌더용
    nextTick(() => {
      setTimeout(() => tableScroll(), 300)
    })
  })

  onBeforeUnmount(() => {
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler)
    }
    if (observer) {
      observer.disconnect()
    }
  })
}
